Ext.define('SA.VectorMapThematicMap', {
    alias: 'widget.vector-map-thematic-map',
    extend: 'Ext.Component',

    cls: 'sa-vector-map-thematic-map',

    afterRender() {
        var {attributionHtml, maxExtent, style, styleBaseUrls, unit, zoomRange} = this;

        this.callParent();

        this.map = patchBoxZoomHandler(new maplibregl.Map({
            boxZoom: false,
            container: this.getEl().dom,
            customAttribution: attributionHtml,
            doubleClickZoom: false,
            dragPan: false,
            dragRotate: false,
            maxBounds: maxExtent.toLngLatBounds(),
            maxZoom: zoomRange.getMaximum(),
            minZoom: zoomRange.getMinimum(),
            style,
            touchPitch: false,
            touchZoomRotate: false,
            trackResize: false,
            transformRequest: SA.VectorMapThematicMapRequestTransformFunction.get(styleBaseUrls)
        }));

        this.loading = false;
        this.targetZoom = this.getZoom();

        this.map.addControl(new maplibregl.ScaleControl({unit}), 'bottom-left');

        this.map.on('data', this.onMapData.bind(this));
        this.map.on('dataabort', this.onMapData.bind(this));
        this.map.on('dataloading', this.onMapData.bind(this));
        this.map.on('error', this.onMapError.bind(this));
        this.map.on('zoom', this.onMapZoom.bind(this));

        this.map.once('styledata', () => {
            this.styleLoaded = true;

            this.refreshGeoJsonSources();
            this.refreshTileSources();

            this.refreshLayersProperties();
        });

        function patchBoxZoomHandler(map) {
            var {mousedown} = map.boxZoom;

            map.boxZoom.mousedown = function(ev, point) {
                mousedown.call(this, new MouseEvent(ev.type, {...ev, shiftKey: true}), point);
            };

            return map;
        }
    },

    clearFeatureState({source}, key) {
        for (let {id} of this.map.querySourceFeatures(source)) {
            let state = this.map.getFeatureState({id, source});

            if (state && Reflect.has(state, key)) {
                this.map.removeFeatureState({id, source}, key);
            }
        }
    },

    clearGeoJsonSourceData(sourceId) {
        if (!this.styleLoaded) {
            return;
        }

        this.map.getSource(sourceId).setData(SA.GeoJson.createEmptyFeatureCollection());
    },

    clearHovered({source}) {
        this.clearFeatureState({source}, 'hovered');
    },

    clearSelected({source}) {
        this.clearFeatureState({source}, 'selected');
    },

    destroy() {
        this.map?.remove();

        this.callParent();
    },

    fitExtent(extent) {
        if (this.getExtent().equals(extent)) {
            return;
        }

        this.map.fitBounds(extent.toLngLatBounds(), {animate: false});
    },

    getExtent() {
        return SA.VectorMapExtent.fromLngLatBounds(this.map.getBounds().toArray());
    },

    getZoom() {
        return this.map.getZoom();
    },

    isLoading() {
        return this.loading;
    },

    isSelected(feature) {
        return this.map.getFeatureState(feature).selected ?? false;
    },

    isUserDrawingZoomBoxOrPanning() {
        return this.map.boxZoom.isActive() || this.map.dragPan.isActive();
    },

    onClick(callback, scope) {
        this.map.on('click', ev => {
            var coordinate = SA.VectorMapCoordinate.fromLngLat(ev.lngLat.toArray()),
                event = ev.originalEvent,
                position = new SA.VectorMapPoint(ev.point.x, ev.point.y);

            callback.call(scope, {coordinate, event, position});
        });
    },

    onHttpError(callback, scope) {
        this.on('httperror', callback, scope);
    },

    onLoadingChange(callback, scope) {
        this.on('change:loading', callback, scope);
    },

    onMapData() {
        var loading = !this.map.isStyleLoaded();

        if (loading !== this.loading) {
            this.loading = loading;

            this.fireEvent('change:loading');
        }
    },

    async onMapError({error}) {
        if (!(error instanceof maplibregl.AJAXError)) {
            console.error(error);

            return;
        }

        var text = await error.body.text();

        this.fireEvent('httperror', JSON.parse(text));

        this.onMapData();
    },

    onMapZoom({isZoomInOrZoomOut}) {
        if (!isZoomInOrZoomOut) {
            this.targetZoom = this.map.getZoom();
        }
    },

    onMove(callback, scope) {
        this.map.on('move', ({suppressMoveEvents}) => {
            if (!suppressMoveEvents) {
                callback.call(scope);
            }
        });
    },

    onMoveEnd(callback, scope) {
        this.map.on('moveend', ({suppressMoveEvents}) => {
            if (!suppressMoveEvents) {
                callback.call(scope);
            }
        });
    },

    onWebGLContextLost(callback, scope) {
        this.map.on('webglcontextlost', () => {
            if (!this.isDestroyed) {
                callback.call(scope);
            }
        });
    },

    queryFeatureAt(position, layer) {
        if (!this.styleLoaded) {
            return null;
        }

        var point = position instanceof SA.VectorMapCoordinate ? this.map.project(position.toLngLat()) : position.toArray();

        return this.map.queryRenderedFeatures(point, {layers: [layer]})[0] ?? null;
    },

    refreshGeoJsonSources() {
        if (!this.styleLoaded || !this.geoJsonSourcesData) {
            return;
        }

        var style = this.map.getStyle();

        Object.entries(this.geoJsonSourcesData)
            .filter(([sourceId, data]) => SA.Json.differ(data, style.sources[sourceId].data))
            .forEach(([sourceId, data]) => this.map.getSource(sourceId).setData(data));
    },

    refreshLayersProperties() {
        if (!this.styleLoaded || !this.layersProperties) {
            return;
        }

        Object.entries(this.layersProperties).forEach(([layerId, properties]) => SA.VectorMapLayerProperties.apply(this.map, layerId, properties));
    },

    refreshTileSources() {
        if (!this.styleLoaded || !this.tileSourcesUrls) {
            return;
        }

        Object.entries(this.tileSourcesUrls).forEach(([sourceId, tiles]) => this.map.getSource(sourceId).setTiles(tiles));
    },

    resize() {
        this.map.resize();
    },

    setActiveToolType(toolType) {
        this.setCursor(toolType.getCursor());

        if (toolType.isDragPan()) {
            this.map.doubleClickZoom.enable();
            this.map.dragPan.enable();
        }
        else {
            this.map.doubleClickZoom.disable();
            this.map.dragPan.disable();
        }

        if (toolType.isDragZoom()) {
            this.map.boxZoom.enable();
        }
        else {
            this.map.boxZoom.disable();
        }
    },

    setCenter(center, {suppressMoveEvents = false} = {}) {
        this.map.setCenter(center.toLngLat(), {suppressMoveEvents});
    },

    setCursor(cursor) {
        this.map.getCanvasContainer().style.cursor = cursor;
    },

    setGeoJsonSourcesData(geoJsonSourcesData) {
        this.geoJsonSourcesData = geoJsonSourcesData;

        this.refreshGeoJsonSources();
    },

    setHovered(feature) {
        this.map.setFeatureState(feature, {hovered: true});
    },

    setLayersProperties(layersProperties) {
        this.layersProperties = layersProperties;

        this.refreshLayersProperties();
    },

    setSelected(feature) {
        this.map.setFeatureState(feature, {selected: true});
    },

    setTileSourcesUrls(tileSourcesUrls) {
        if (SA.Json.differ(tileSourcesUrls, this.tileSourcesUrls)) {
            this.tileSourcesUrls = tileSourcesUrls;

            this.refreshTileSources();
        }
    },

    setZoom(zoom, {suppressMoveEvents = false} = {}) {
        this.map.setZoom(zoom, {suppressMoveEvents});
    },

    zoomIn() {
        this.targetZoom = Math.min(Math.floor(this.targetZoom + 1), this.map.getMaxZoom());

        this.map.zoomTo(this.targetZoom, null, {isZoomInOrZoomOut: true});
    },

    zoomOut() {
        this.targetZoom = Math.max(Math.ceil(this.targetZoom - 1), this.map.getMinZoom());

        this.map.zoomTo(this.targetZoom, null, {isZoomInOrZoomOut: true});
    }
});